/******************************************************************************
 * dashboardPeriod: today / month / year windows for the home dashboard.      *
 *****************************************************************************/

import { endOfDay, endOfMonth, endOfYear, format, startOfDay, startOfMonth, startOfYear } from "date-fns";

export type DashboardPeriod = "today" | "month" | "year";

export const DASHBOARD_PERIODS: DashboardPeriod[] = ["today", "month", "year"];

export interface DashboardRange {
  from: Date;
  to: Date;
}

/**
 * resolveDashboardPeriod: the {from, to} a dashboard preset stands for.
 *
 * `today` is injectable so tests can pin the clock.
 */
export function resolveDashboardPeriod(
  period: DashboardPeriod,
  today: Date = new Date()
): DashboardRange {
  switch (period) {
  case "month":
    return { from: startOfMonth(today), to: endOfMonth(today) };
  case "year":
    return { from: startOfYear(today), to: endOfYear(today) };
  case "today":
  default:
    return { from: startOfDay(today), to: endOfDay(today) };
  }
}

/** Local `yyyy-MM-dd` as `GET /stats` expects it. */
export function toLocalDate(date: Date): string {
  return format(date, "yyyy-MM-dd");
}

/** Local `yyyy-MM-dd'T'HH:mm:ss` as `GET .../unavailable` expects it. */
export function toLocalDateTime(date: Date): string {
  return format(date, "yyyy-MM-dd'T'HH:mm:ss");
}

/** Hours and minutes, same notation as the rest of the app (`1 h 30`, `45 min`). */
export function formatDurationMinutes(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return "—";
  const total = Math.round(value);
  const hours = Math.floor(total / 60);
  const minutes = total % 60;
  if (hours === 0) return `${minutes} min`;
  return `${hours} h ${String(minutes).padStart(2, "0")}`;
}
